import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import TimezoneSelect from "@/components/ui/timezone-select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import EmployeeDocumentsTab from "@/components/employee/EmployeeDocumentsTab";
import {
  ArrowLeft, Briefcase, Calendar, Wallet, IdCard, Save, Mail, Phone,
  MapPin, Users, ChevronRight, Trash2, AlertTriangle, Sparkles, RefreshCw,
  ShieldCheck, ShieldAlert, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import { CreateLinkUserDialog } from "@/pages/app/Employees";
import { EmployeeDesignationPropagationDialog } from "@/pages/app/hr/DesignationPropagationDialog";

const EMPLOYMENT_TYPES = ["full_time", "part_time", "contract", "intern"];
const STATUSES = ["active", "onboarding", "notice", "inactive"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AUD", "CAD", "AED", "SGD"];
const PAY_CYCLES = ["monthly", "biweekly", "weekly"];

// ─────────────────── R148m · Feb 2026 · Employee Change Log ───────────────────
function _formatChangeValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
import CompOffLedger from "@/components/employees/CompOffLedger";

function EmployeeChangeLog({ eid }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/employees/${eid}/audit`);
        if (alive) setRows(data || []);
      } catch (e) { toast.error(apiErrorMessage(e, "Failed to load change log")); if (alive) setRows([]); }
      finally { if (alive) setBusy(false); }
    })();
    return () => { alive = false; };
  }, [eid]);
  if (busy) return <div className="p-4 text-xs text-muted-foreground" data-testid="employee-changelog-loading">Loading change log…</div>;
  if (!rows || rows.length === 0) {
    return (
      <div className="border border-border p-6 text-center text-sm text-muted-foreground" data-testid="employee-changelog-empty">
        No edits recorded for this employee yet. Any HR change from now on will appear here with actor + timestamp.
      </div>
    );
  }
  return (
    <div className="border border-border" data-testid="employee-changelog-list">
      <div className="grid grid-cols-[9rem_10rem_1fr_1fr_9rem] gap-2 px-3 py-2 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
        <div>Actor</div><div>Field</div><div>Was</div><div>Now</div><div className="text-right">When</div>
      </div>
      <ul className="divide-y divide-border">
        {rows.map((r) => (
          <li key={r.id} className="grid grid-cols-[9rem_10rem_1fr_1fr_9rem] gap-2 px-3 py-2 text-[11px] hover:bg-muted/20" data-testid={`employee-changelog-row-${r.id}`}>
            <div className="truncate" title={r.actor_name}>{r.actor_name || "—"}</div>
            <div className="font-mono text-[10px] text-primary">{r.field}</div>
            <div className="text-rose-700 line-through truncate" title={_formatChangeValue(r.old)}>{_formatChangeValue(r.old)}</div>
            <div className="text-emerald-700 truncate" title={_formatChangeValue(r.new)}>{_formatChangeValue(r.new)}</div>
            <div className="text-[10px] text-muted-foreground text-right whitespace-nowrap" title={r.at}>{fmtRelative(r.at)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────── R148m · Feb 2026 · Geocode Assist ───────────────────
async function reverseGeocode(address) {
  // Nominatim (OpenStreetMap) — free, no key required.
  if (!address || address.trim().length < 4) return null;
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address)}`;
  const res = await fetch(url, { headers: { "Accept": "application/json" } });
  if (!res.ok) throw new Error(`Nominatim ${res.status}`);
  const arr = await res.json();
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const { lat, lon } = arr[0];
  return { lat: Number(lat), lng: Number(lon) };
}
function PinOnMapButton({ address, onPin, testid }) {
  const [busy, setBusy] = useState(false);
  const disabled = !address || address.trim().length < 4 || busy;
  const run = async () => {
    setBusy(true);
    try {
      const hit = await reverseGeocode(address);
      if (!hit) { toast.info("Couldn't locate this address. Please refine the text or pin manually."); return; }
      onPin?.(hit);
      toast.success(`Pinned at ${hit.lat.toFixed(4)}, ${hit.lng.toFixed(4)}`);
    } catch (e) { toast.error("Geocoding failed. Please try again."); console.error(e); }
    finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={run}
      className="inline-flex items-center gap-1 border border-primary/40 bg-primary/5 hover:bg-primary/10 text-primary text-[10px] px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
      title={disabled && !busy ? "Type an address first" : "Reverse-geocode via OpenStreetMap Nominatim"}
      data-testid={testid || "pin-address-on-map-btn"}
    >
      <MapPin className="w-3 h-3" /> {busy ? "Pinning…" : "Pin address on map"}
    </button>
  );
}



/**
 * AddressProofRow — R148g · Feb 2026
 * ----------------------------------
 * Shows Current + Permanent addresses side-by-side with a verified /
 * unverified proof chip pulled from `employee_documents`. Clicking the
 * chip deep-links into the Documents tab. Keeps the profile page
 * scannable without hiding the compliance signal.
 */
function AddressProofChip({ doc, label, employeeId }) {
  let cls = "border-border bg-muted/40 text-muted-foreground";
  let icon = <FileText className="w-3 h-3" />;
  let text = `${label}: no proof`;
  if (doc?.attachment_id && doc.verified) {
    cls = "border-emerald-500 bg-emerald-50 text-emerald-700";
    icon = <ShieldCheck className="w-3 h-3" />;
    text = `${label}: verified`;
  } else if (doc?.attachment_id) {
    cls = "border-amber-500 bg-amber-50 text-amber-800";
    icon = <ShieldAlert className="w-3 h-3" />;
    text = `${label}: uploaded, not verified`;
  }
  return (
    <Link
      to={`/app/employees/${employeeId}?tab=documents`}
      className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-semibold hover:opacity-80 ${cls}`}
      title="Open Documents tab to upload / verify"
      data-testid={`address-proof-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      {icon} {text}
    </Link>
  );
}

function AddressMapEmbed({ address, lat, lng, testid }) {
  // R148h · Feb 2026 — Free embedded map preview.
  //   • If we have lat+lng: pin exactly there via OpenStreetMap bbox embed
  //     (no API key, no CORS).
  //   • Otherwise: pin the geocoded address via Google Maps' unauthenticated
  //     "?output=embed" iframe. Free, no key. Falls back to a text link.
  const hasLatLng = lat != null && lng != null && lat !== "" && lng !== "" && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng));
  if (!hasLatLng && !address) return null;
  let src, deepLink;
  if (hasLatLng) {
    const la = Number(lat); const ln = Number(lng);
    const d = 0.006;
    const bbox = `${ln - d}%2C${la - d}%2C${ln + d}%2C${la + d}`;
    src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${la}%2C${ln}`;
    deepLink = `https://www.openstreetmap.org/?mlat=${la}&mlon=${ln}#map=17/${la}/${ln}`;
  } else {
    const q = encodeURIComponent(address);
    src = `https://www.google.com/maps?q=${q}&output=embed`;
    deepLink = `https://www.google.com/maps?q=${q}`;
  }
  return (
    <div className="mt-1 ml-5 border border-border overflow-hidden" data-testid={testid}>
      <iframe
        title={testid || "address-map"}
        src={src}
        width="100%"
        height="160"
        loading="lazy"
        style={{ border: 0 }}
        referrerPolicy="no-referrer-when-downgrade"
      />
      <a
        href={deepLink}
        target="_blank"
        rel="noopener noreferrer"
        className="block text-[10px] text-primary hover:underline px-2 py-1 bg-muted/40"
        data-testid={`${testid}-deeplink`}
      >
        Open in {hasLatLng ? "OpenStreetMap" : "Google Maps"} ↗
      </a>
    </div>
  );
}

function AddressProofRow({ wsId, employeeId, userId, currentAddress, permanentAddress, currentLat, currentLng, permanentLat, permanentLng }) {
  const [docs, setDocs] = useState({});
  useEffect(() => {
    if (!wsId || !userId) return;
    let cancel = false;
    api.get(`/workspaces/${wsId}/employee-documents`, { params: { user_id: userId } })
      .then(({ data }) => {
        if (cancel) return;
        const by = {};
        for (const r of (data?.rows || [])) {
          if (r.category === "address_proof") by[r.subtype] = r;
        }
        setDocs(by);
      })
      .catch(() => {});
    return () => { cancel = true; };
  }, [wsId, userId]);

  return (
    <div className="border-t border-border/40 pt-2 mt-1 space-y-2" data-testid="employee-address-proof-block">
      {currentAddress && (
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Current address</span>
            {userId && <AddressProofChip doc={docs.current_address} label="Current proof" employeeId={employeeId} />}
          </div>
          <div className="text-sm pl-5 text-foreground/80">{currentAddress}</div>
          <AddressMapEmbed
            address={currentAddress}
            lat={currentLat}
            lng={currentLng}
            testid="address-map-current"
          />
        </div>
      )}
      {permanentAddress && (
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Permanent address</span>
            {userId && <AddressProofChip doc={docs.permanent_address} label="Permanent proof" employeeId={employeeId} />}
          </div>
          <div className="text-sm pl-5 text-foreground/80">{permanentAddress}</div>
          <AddressMapEmbed
            address={permanentAddress}
            lat={permanentLat}
            lng={permanentLng}
            testid="address-map-permanent"
          />
        </div>
      )}
    </div>
  );
}

export default function EmployeeDetail() {
  const { eid } = useParams();
  const navigate = useNavigate();
  const { activeWorkspaceId, user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [linkUserOpen, setLinkUserOpen] = useState(false);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [form, setForm] = useState({});
  // Iter Feb 2026 R19 · HR designations master + propagation dialog state
  const [designations, setDesignations] = useState([]);
  // Feb 2026 R100 · Departments master so the Department field is a proper
  // dropdown instead of a free-text input.
  const [departments, setDepartments] = useState([]);
  // Oct 2026 · Shifts master so Role & reporting can assign a shift.
  const [shifts, setShifts] = useState([]);
  const [propOpen, setPropOpen] = useState(false);
  const [propCtx, setPropCtx] = useState(null);

  const load = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.get(`/employees/${eid}`);
      setEmp(data);
      setForm(buildForm(data));
      setDirty(false);
    } catch (e) {
      setErr(apiErrorMessage(e, "Could not load employee"));
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [eid]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/members`).then((r) => setMembers(r.data || [])).catch(() => setMembers([]));
    api.get(`/workspaces/${activeWorkspaceId}/roles`).then((r) => setRoles(r.data || [])).catch(() => setRoles([]));
    // Iter Feb 2026 R19 · Fetch HR designations master (with duty templates)
    api.get(`/workspaces/${activeWorkspaceId}/hr/designations`)
      .then((r) => setDesignations(r.data || []))
      .catch(() => setDesignations([]));
    // R100 · Departments master → drives the Department dropdown.
    api.get(`/workspaces/${activeWorkspaceId}/departments`)
      .then((r) => setDepartments(r.data || []))
      .catch(() => setDepartments([]));
    // Oct 2026 · Shifts master (HR → Leave & Attendance) feeds the
    // Role & reporting shift picker. Falls back to empty list so the
    // form still saves even when HR hasn't configured shifts yet.
    api.get(`/workspaces/${activeWorkspaceId}/shifts`)
      .then((r) => setShifts(r.data || []))
      .catch(() => setShifts([]));
  }, [activeWorkspaceId]);

  const set = (k, v) => { setForm((s) => ({ ...s, [k]: v })); setDirty(true); };

  const save = async () => {
    try {
      const payload = { ...form };
      // Numeric coercion — empty string → null; numeric fields → Number
      const numericFields = [
        "leave_balance_sick", "leave_balance_casual", "leave_balance_earned",
        "salary_amount", "previous_ctc", "total_experience_years",
        "dependent_count", "notice_period_days",
        "current_lat", "current_lng", "permanent_lat", "permanent_lng",
      ];
      numericFields.forEach((k) => {
        if (payload[k] === "" || payload[k] == null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      ["reporting_manager_id", "user_id", "shift_id"].forEach((k) => { if (!payload[k]) payload[k] = null; });
      // Parse custom_fields textarea "key: value" lines into an object.
      if (payload._custom_fields_text !== undefined) {
        const text = String(payload._custom_fields_text || "");
        const out = {};
        text.split(/\n+/).forEach((line) => {
          const idx = line.indexOf(":");
          if (idx > 0) {
            const k = line.slice(0, idx).trim();
            const v = line.slice(idx + 1).trim();
            if (k) out[k] = v;
          }
        });
        payload.custom_fields = out;
        delete payload._custom_fields_text;
      }
      // Iter Feb 2026 R19 · Detect designation change to prompt propagation
      const oldDid = emp?.designation_id || null;
      const newDid = payload.designation_id || null;
      const designationChanged = oldDid !== newDid && !!emp?.user_id;

      await api.patch(`/employees/${eid}`, payload);
      toast.success("Employee updated");

      if (designationChanged) {
        const oldD = designations.find((d) => d.id === oldDid);
        const newD = designations.find((d) => d.id === newDid);
        setPropCtx({
          userId: emp.user_id,
          oldDesignation: oldD ? { id: oldD.id, name: oldD.name } : (emp?.designation ? { id: oldDid, name: emp.designation } : null),
          newDesignation: newD ? { id: newD.id, name: newD.name } : null,
        });
        setPropOpen(true);
      } else {
        await load();
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const removeEmployee = async () => {
    if (!window.confirm(`Remove ${emp?.full_name || "this employee"} from the master record?`)) return;
    try {
      await api.delete(`/employees/${eid}`);
      toast.success("Employee removed");
      navigate("/app/employees");
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const initials = useMemo(() => {
    if (!emp?.full_name) return "—";
    return emp.full_name.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  }, [emp]);

  if (err) {
    return (
      <div className="px-4 sm:px-6 md:px-10 py-12">
        <div className="border border-border p-8 max-w-xl" data-testid="employee-detail-error">
          <AlertTriangle className="w-6 h-6 text-primary mb-3" />
          <div className="font-bold text-lg mb-2">Could not open this employee</div>
          <div className="text-sm text-foreground/70 mb-4">{err}</div>
          <Link to="/app/employees"><Button variant="outline" className="rounded-none" data-testid="employee-detail-back-error">Back to employees</Button></Link>
        </div>
      </div>
    );
  }

  if (busy || !emp) {
    return <div className="px-4 sm:px-6 md:px-10 py-8 text-sm" data-testid="employee-detail-loading">Loading employee…</div>;
  }

  return (
    <>
      <PageHeader
        eyebrow={emp.emp_id ? `Employee · ${emp.emp_id}` : "Employee"}
        title={emp.full_name}
        description={[emp.designation, emp.department].filter(Boolean).join(" · ") || "Employee detail"}
        testid="employee-detail-header"
        actions={
          <div className="flex gap-2">
            <Link to="/app/employees">
              <Button variant="outline" className="rounded-none" data-testid="employee-detail-back">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </Link>
            <Button onClick={save} disabled={!dirty} className="rounded-none" data-testid="employee-detail-save">
              <Save className="w-4 h-4 mr-2" /> {dirty ? "Save changes" : "Saved"}
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-6 grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Sidebar card */}
        <aside className="xl:col-span-4 space-y-4">
          <div className="border border-border p-6" data-testid="employee-detail-card">
            <div className="flex items-center gap-4 mb-5">
              {emp.user?.avatar_data_url ? (
                <img src={emp.user.avatar_data_url} alt={emp.full_name} className="w-16 h-16 rounded-none object-cover border border-border" />
              ) : (
                <div className="w-16 h-16 rounded-none bg-foreground text-background flex items-center justify-center text-2xl font-black" style={{ fontFamily: "var(--font-heading)" }}>
                  {initials}
                </div>
              )}
              <div className="min-w-0">
                <div className="font-bold text-lg leading-tight truncate" data-testid="employee-detail-name">{emp.full_name}</div>
                <div className="text-xs text-foreground/60 truncate">{emp.designation || "—"}</div>
                <div className="mt-2"><span className="chip text-[10px]" data-testid="employee-detail-status">{emp.status}</span></div>
              </div>
            </div>
            <dl className="text-sm space-y-3">
              <Row icon={Mail} label="Work email" value={emp.work_email} testid="row-email" />
              <Row icon={Phone} label="Phone" value={emp.phone} testid="row-phone" />
              <Row icon={Briefcase} label="Department" value={emp.department} testid="row-department" />
              <Row icon={IdCard} label="Employment type" value={(emp.employment_type || "").replace(/_/g, " ") || null} testid="row-employment" />
              <Row icon={Calendar} label="Joined" value={emp.joining_date ? `${emp.joining_date} · ${fmtRelative(emp.joining_date)}` : null} testid="row-joined" />
              <Row icon={MapPin} label="Address" value={emp.address} testid="row-address" />
              {/* R148g · Feb 2026 — Show Current + Permanent addresses
                  side-by-side with their proof-verification status.
                  Actual upload lives in the Documents tab; this is a
                  quick "at-a-glance" summary + shortcut. */}
              {(emp.current_address || emp.permanent_address) && (
                <AddressProofRow
                  wsId={emp.workspace_id}
                  employeeId={emp.id}
                  userId={emp.user_id}
                  currentAddress={emp.current_address}
                  permanentAddress={emp.permanent_address}
                  currentLat={emp.current_lat}
                  currentLng={emp.current_lng}
                  permanentLat={emp.permanent_lat}
                  permanentLng={emp.permanent_lng}
                />
              )}
            </dl>

            {emp.reporting_manager?.name && (
              <div className="mt-5 pt-5 border-t border-border" data-testid="employee-detail-manager">
                <div className="label-eyebrow mb-2">Reports to</div>
                {emp.reporting_manager.employee_id ? (
                  <Link
                    to={`/app/employees/${emp.reporting_manager.employee_id}`}
                    className="flex items-center justify-between hover:bg-muted/60 p-2 -mx-2"
                    data-testid="employee-detail-manager-link"
                  >
                    <div>
                      <div className="font-semibold text-sm">{emp.reporting_manager.name}</div>
                      <div className="text-xs text-foreground/60">{emp.reporting_manager.designation || "—"}</div>
                    </div>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <div>
                    <div className="font-semibold text-sm">{emp.reporting_manager.name}</div>
                    <div className="text-xs text-foreground/60">{emp.reporting_manager.designation || "—"}</div>
                  </div>
                )}
              </div>
            )}

            {(emp.direct_reports || []).length > 0 && (
              <div className="mt-5 pt-5 border-t border-border" data-testid="employee-detail-reports">
                <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Direct reports · {emp.direct_reports.length}</div>
                <ul className="space-y-1">
                  {emp.direct_reports.map((r) => (
                    <li key={r.id}>
                      <Link
                        to={`/app/employees/${r.id}`}
                        className="flex items-center justify-between hover:bg-muted/60 p-2 -mx-2 text-sm"
                        data-testid={`employee-detail-report-${r.id}`}
                      >
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{r.full_name}</div>
                          <div className="text-xs text-foreground/60 truncate">{r.designation || "—"}</div>
                        </div>
                        <ChevronRight className="w-4 h-4 shrink-0" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="border border-border p-4">
            <div className="label-eyebrow mb-2">Linked user account</div>
            {emp.user ? (
              <div className="text-sm" data-testid="employee-detail-user-linked">
                <div className="font-semibold">{emp.user.name || "—"}</div>
                <div className="text-xs text-foreground/60">{emp.user.email}</div>
                <span className="chip chip-internal text-[10px] mt-2 inline-block">App access granted</span>
              </div>
            ) : (
              <div className="text-xs text-foreground/60" data-testid="employee-detail-user-unlinked">
                No linked user account. Use the “Role & reporting” tab to link this employee to a workspace member.
              </div>
            )}
          </div>

          <Button variant="outline" className="rounded-none w-full text-destructive hover:text-destructive" onClick={removeEmployee} data-testid="employee-detail-delete">
            <Trash2 className="w-4 h-4 mr-2" /> Remove from records
          </Button>
        </aside>

        {/* Tabbed editor */}
        <main className="xl:col-span-8">
          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="rounded-none border border-border bg-muted/30 mb-4" data-testid="employee-detail-tabs">
              <TabsTrigger value="profile" data-testid="employee-tab-profile">Profile</TabsTrigger>
              <TabsTrigger value="role" data-testid="employee-tab-role">Role &amp; reporting</TabsTrigger>
              <TabsTrigger value="leave" data-testid="employee-tab-leave">Leave</TabsTrigger>
              <TabsTrigger value="payroll" data-testid="employee-tab-payroll">Payroll</TabsTrigger>
              {emp.user_id && <TabsTrigger value="documents" data-testid="employee-tab-documents">Documents</TabsTrigger>}
              <TabsTrigger value="family" data-testid="employee-tab-family">Family &amp; Statutory</TabsTrigger>
              <TabsTrigger value="history" data-testid="employee-tab-history">History</TabsTrigger>
              <TabsTrigger value="custom" data-testid="employee-tab-custom">Custom</TabsTrigger>
              <TabsTrigger value="changes" data-testid="employee-tab-changes">Changes</TabsTrigger>
              {emp.user_id && <TabsTrigger value="performance" data-testid="employee-tab-performance">Performance</TabsTrigger>}
            </TabsList>

            {emp.user_id && (
              <TabsContent value="documents" className="m-0">
                <EmployeeDocumentsTab wsId={emp.workspace_id} userId={emp.user_id} />
              </TabsContent>
            )}

            {emp.user_id && (
              <TabsContent value="performance" className="m-0">
                <EmployeePerformance userId={emp.user_id} />
              </TabsContent>
            )}

            <TabsContent value="profile" className="m-0">
              <Section icon={Briefcase} title="Identity">
                <Row3>
                  <Field label="Employee ID" value={form.emp_id} onChange={(v) => set("emp_id", v)} testid="ed-empid" placeholder="EMP-001" />
                  <Field label="Full name *" value={form.full_name} onChange={(v) => set("full_name", v)} testid="ed-name" />
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 inline-block">Designation</Label>
                    <select
                      value={form.designation_id || ""}
                      onChange={(e) => {
                        const did = e.target.value;
                        const d = designations.find((x) => x.id === did);
                        setForm((s) => ({ ...s, designation_id: did, designation: d?.name || "" }));
                        setDirty(true);
                      }}
                      className="h-9 w-full border border-border rounded-none px-2 text-xs bg-background"
                      data-testid="ed-designation"
                    >
                      <option value="">— Select designation —</option>
                      {designations.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                          {d.default_duties?.length ? ` · ${d.default_duties.length} duties` : ""}
                        </option>
                      ))}
                      {form.designation && !form.designation_id && (
                        <option value="" disabled>{form.designation} (legacy custom · pick canonical)</option>
                      )}
                    </select>
                    {form.designation_id && (
                      <p className="text-[9px] text-muted-foreground mt-1">
                        Job duties from this designation are auto-assigned. Change to trigger the propagation dialog.
                      </p>
                    )}
                    {designations.length === 0 && (
                      <p className="text-[9px] text-muted-foreground mt-1">
                        No designations. Add them under <a href="/app/hr/designations" className="text-primary hover:underline">HR → Designations</a>.
                      </p>
                    )}
                  </div>
                </Row3>
                <Row3>
                  <Field label="Work email" type="email" value={form.work_email} onChange={(v) => set("work_email", v)} testid="ed-email" />
                  <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testid="ed-phone" />
                  {/* R100 · Department dropdown driven by Departments master.
                      Falls back to a free-text input when no departments
                      exist yet (older workspaces). */}
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 inline-block">Department</Label>
                    {departments.length > 0 ? (
                      <select
                        value={form.department || ""}
                        onChange={(e) => set("department", e.target.value)}
                        className="h-9 w-full border border-border rounded-none px-2 text-xs bg-background"
                        data-testid="ed-department"
                      >
                        <option value="">— Select department —</option>
                        {departments.map((d) => (
                          <option key={d.id} value={d.name}>{d.name}</option>
                        ))}
                        {form.department && !departments.some((d) => d.name === form.department) && (
                          <option value={form.department}>{form.department} (legacy · not in master)</option>
                        )}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={form.department || ""}
                        onChange={(e) => set("department", e.target.value)}
                        className="h-9 w-full border border-border rounded-none px-2 text-xs bg-background"
                        placeholder="Add departments in Org settings…"
                        data-testid="ed-department"
                      />
                    )}
                    <p className="text-[9px] text-muted-foreground mt-1">
                      Manage the list under <a href="/app/settings/org" className="text-primary hover:underline">Settings → Org → Departments</a>.
                    </p>
                  </div>
                </Row3>
                {/* R100 · Personal timezone. Remote hires see their check-in
                    time in their own local tz; when empty the workspace tz
                    is used. Attendance late-minutes calculation honours the
                    same order (see routes_attendance._effective_user_tz). */}
                <Row3>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 inline-block">Personal timezone</Label>
                    <TimezoneSelect
                      value={form.timezone || ""}
                      onChange={(v) => set("timezone", v || null)}
                      testid="ed-timezone"
                    />
                    <p className="text-[9px] text-muted-foreground mt-1">
                      Overrides the workspace timezone for this employee's attendance and profile displays. Leave blank to inherit.
                    </p>
                  </div>
                </Row3>
                <Field label="Address" value={form.address} onChange={(v) => set("address", v)} testid="ed-address" placeholder="Postal address" />
              </Section>

              <Section icon={IdCard} title="Personal details">
                <Row3>
                  <Field label="Date of birth" type="date" value={form.date_of_birth?.slice(0, 10) || ""} onChange={(v) => set("date_of_birth", v)} testid="ed-dob" />
                  <SelectField
                    label="Gender"
                    value={form.gender}
                    onChange={(v) => set("gender", v)}
                    options={[{ v: "male", label: "Male" }, { v: "female", label: "Female" }, { v: "other", label: "Other" }, { v: "prefer_not_to_say", label: "Prefer not to say" }]}
                    testid="ed-gender"
                  />
                  <SelectField
                    label="Marital status"
                    value={form.marital_status}
                    onChange={(v) => set("marital_status", v)}
                    options={[{ v: "single", label: "Single" }, { v: "married", label: "Married" }, { v: "divorced", label: "Divorced" }, { v: "widowed", label: "Widowed" }]}
                    testid="ed-marital"
                  />
                </Row3>
                <Row3>
                  <Field label="Nationality" value={form.nationality} onChange={(v) => set("nationality", v)} testid="ed-nationality" placeholder="Indian" />
                  <SelectField
                    label="Blood group"
                    value={form.blood_group}
                    onChange={(v) => set("blood_group", v)}
                    options={["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((b) => ({ v: b, label: b }))}
                    testid="ed-blood"
                  />
                  <Field label="National ID (Aadhaar / SSN)" value={form.national_id} onChange={(v) => set("national_id", v)} testid="ed-national-id" />
                </Row3>
                <Row3>
                  <Field label="Wedding anniversary" type="date" value={form.wedding_anniversary?.slice(0, 10) || ""} onChange={(v) => set("wedding_anniversary", v)} testid="ed-anniversary" />
                  <div className="flex items-end pb-2">
                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer" data-testid="ed-celebrations-optout-wrap">
                      <input type="checkbox" checked={!!form.celebrations_opt_out} onChange={(e) => set("celebrations_opt_out", e.target.checked)} data-testid="ed-celebrations-optout" />
                      Opt out of birthday/anniversary announcements
                    </label>
                  </div>
                </Row3>
              </Section>

              <Section icon={MapPin} title="Addresses (with map)">
                <div>
                  <Label className="block mb-1.5 text-sm font-semibold">Current address</Label>
                  <Textarea rows={2} value={form.current_address || ""} onChange={(e) => set("current_address", e.target.value)} className="rounded-none" data-testid="ed-current-address" placeholder="Street, city, state, PIN" />
                  <div className="flex items-center gap-2 mt-1.5">
                    <PinOnMapButton
                      address={form.current_address}
                      onPin={({ lat, lng }) => { set("current_lat", lat); set("current_lng", lng); }}
                      testid="ed-pin-current"
                    />
                    {form.current_lat != null && form.current_lat !== "" && (
                      <span className="text-[10px] text-muted-foreground">Pinned · lat/lng below</span>
                    )}
                  </div>
                  <Row2>
                    <Field label="Current latitude" type="number" value={form.current_lat} onChange={(v) => set("current_lat", v)} testid="ed-current-lat" placeholder="12.9716" />
                    <Field label="Current longitude" type="number" value={form.current_lng} onChange={(v) => set("current_lng", v)} testid="ed-current-lng" placeholder="77.5946" />
                  </Row2>
                  <AddressMapEmbed
                    address={form.current_address}
                    lat={form.current_lat}
                    lng={form.current_lng}
                    testid="ed-current-address-map"
                  />
                </div>
                <div className="pt-2 border-t border-border/50">
                  <Label className="block mb-1.5 text-sm font-semibold">Permanent address</Label>
                  <Textarea rows={2} value={form.permanent_address || ""} onChange={(e) => set("permanent_address", e.target.value)} className="rounded-none" data-testid="ed-permanent-address" placeholder="Street, city, state, PIN" />
                  <div className="flex items-center gap-2 mt-1.5">
                    <PinOnMapButton
                      address={form.permanent_address}
                      onPin={({ lat, lng }) => { set("permanent_lat", lat); set("permanent_lng", lng); }}
                      testid="ed-pin-permanent"
                    />
                    {form.permanent_lat != null && form.permanent_lat !== "" && (
                      <span className="text-[10px] text-muted-foreground">Pinned · lat/lng below</span>
                    )}
                  </div>
                  <Row2>
                    <Field label="Permanent latitude" type="number" value={form.permanent_lat} onChange={(v) => set("permanent_lat", v)} testid="ed-permanent-lat" placeholder="12.9716" />
                    <Field label="Permanent longitude" type="number" value={form.permanent_lng} onChange={(v) => set("permanent_lng", v)} testid="ed-permanent-lng" placeholder="77.5946" />
                  </Row2>
                  <AddressMapEmbed
                    address={form.permanent_address}
                    lat={form.permanent_lat}
                    lng={form.permanent_lng}
                    testid="ed-permanent-address-map"
                  />
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Click <b>Pin address on map</b> to auto-fill lat/lng from the text — free, no key, powered by OpenStreetMap.
                </p>
              </Section>

              <Section icon={Phone} title="Emergency contact">
                <Row2>
                  <Field label="Contact name" value={form.emergency_contact_name} onChange={(v) => set("emergency_contact_name", v)} testid="ed-em-name" />
                  <Field label="Relation" value={form.emergency_contact_relation} onChange={(v) => set("emergency_contact_relation", v)} testid="ed-em-relation" placeholder="Spouse / Parent / Sibling" />
                </Row2>
                <Row2>
                  <Field label="Phone" value={form.emergency_contact_phone} onChange={(v) => set("emergency_contact_phone", v)} testid="ed-em-phone" />
                  <Field label="Email" type="email" value={form.emergency_contact_email} onChange={(v) => set("emergency_contact_email", v)} testid="ed-em-email" />
                </Row2>
              </Section>
            </TabsContent>

            <TabsContent value="role" className="m-0">
              <Section icon={Users} title="Role &amp; reporting line">
                <Row3>
                  <SelectField label="Employment type" value={form.employment_type} onChange={(v) => set("employment_type", v)} options={EMPLOYMENT_TYPES.map((t) => ({ v: t, label: t.replace("_", " ") }))} testid="ed-employment-type" />
                  <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} options={STATUSES.map((s) => ({ v: s, label: s }))} testid="ed-status" />
                  <Field label="Joining date" type="date" value={form.joining_date?.slice(0, 10) || ""} onChange={(v) => set("joining_date", v)} testid="ed-joining" />
                </Row3>
                <Row2>
                  <SelectField
                    label="Reporting manager"
                    value={form.reporting_manager_id}
                    onChange={(v) => set("reporting_manager_id", v)}
                    options={members.map((m) => ({ v: m.user_id, label: m.user?.name || m.user?.email || "—" }))}
                    testid="ed-manager"
                  />
                  <div>
                    <SelectField
                      label="Linked user account (grants app access)"
                      value={form.user_id}
                      onChange={(v) => set("user_id", v)}
                      options={members.map((m) => ({ v: m.user_id, label: m.user?.name || m.user?.email || "—" }))}
                      testid="ed-user-link"
                    />
                    {!form.user_id && (
                      <button
                        type="button"
                        className="mt-1.5 text-[11px] text-primary hover:underline inline-flex items-center gap-1"
                        onClick={() => setLinkUserOpen(true)}
                        data-testid="ed-create-link-user-btn"
                      >
                        <Sparkles className="w-3 h-3" /> Create &amp; link a new user account for this employee
                      </button>
                    )}
                  </div>
                </Row2>
                {/* Oct 2026 · Shift assignment. Dropdown is populated
                    from HR → Leave & Attendance → Shifts. When left
                    blank, the user falls back to the workspace's
                    default shift. */}
                <Row2>
                  <div>
                    <SelectField
                      label="Shift"
                      value={form.shift_id}
                      onChange={(v) => set("shift_id", v)}
                      options={shifts.map((s) => ({
                        v: s.id,
                        label: `${s.name} · ${s.start_time || "09:00"}–${s.end_time || "18:00"}${s.is_default ? "  (default)" : ""}`,
                      }))}
                      testid="ed-shift"
                      placeholder={shifts.length ? "Use workspace default" : "No shifts configured yet"}
                    />
                    {!shifts.length && (
                      <div className="mt-1.5 text-[11px] text-muted-foreground">
                        No shifts configured yet. Add shifts under HR → Leave &amp; Attendance.
                      </div>
                    )}
                  </div>
                  <div />
                </Row2>
              </Section>

              <Section icon={Calendar} title="Lifecycle dates">
                <Row3>
                  <Field label="Probation end" type="date" value={form.probation_end_date?.slice(0, 10) || ""} onChange={(v) => set("probation_end_date", v)} testid="ed-probation-end" />
                  <Field label="Confirmation date" type="date" value={form.confirmation_date?.slice(0, 10) || ""} onChange={(v) => set("confirmation_date", v)} testid="ed-confirmation-date" />
                  <Field label="Exit date" type="date" value={form.exit_date?.slice(0, 10) || ""} onChange={(v) => set("exit_date", v)} testid="ed-exit-date" />
                </Row3>
                <Row3>
                  <Field label="On notice until" type="date" value={form.on_notice_until?.slice(0, 10) || ""} onChange={(v) => set("on_notice_until", v)} testid="emp-on-notice-until" placeholder="YYYY-MM-DD" />
                  <Field label="Exit reason" value={form.exit_reason} onChange={(v) => set("exit_reason", v)} testid="ed-exit-reason" placeholder="Resignation / Termination / Retirement…" />
                  <div />
                </Row3>
              </Section>
            </TabsContent>

            <TabsContent value="family" className="m-0" data-testid="employee-family-panel">
              <Section icon={Users} title="Family & dependents">
                <Row3>
                  <Field label="Father's name" value={form.father_name} onChange={(v) => set("father_name", v)} testid="ed-father" />
                  <Field label="Mother's name" value={form.mother_name} onChange={(v) => set("mother_name", v)} testid="ed-mother" />
                  <Field label="Spouse's name" value={form.spouse_name} onChange={(v) => set("spouse_name", v)} testid="ed-spouse" />
                </Row3>
                <Row3>
                  <Field label="Spouse's DOB" type="date" value={form.spouse_dob?.slice(0, 10) || ""} onChange={(v) => set("spouse_dob", v)} testid="ed-spouse-dob" />
                  <Field label="# of dependents" type="number" value={form.dependent_count} onChange={(v) => set("dependent_count", v)} testid="ed-dependent-count" />
                  <div />
                </Row3>
                <div>
                  <Label className="block mb-1.5 text-sm font-semibold">Dependent details</Label>
                  <Textarea rows={3} value={form.dependent_details || ""} onChange={(e) => set("dependent_details", e.target.value)} className="rounded-none" data-testid="ed-dependent-details" placeholder="Names, relations, ages of dependents (one per line)" />
                </div>
              </Section>

              <Section icon={IdCard} title="Statutory IDs">
                <Row3>
                  <Field label="UAN" value={form.uan} onChange={(v) => set("uan", v)} testid="ed-uan" />
                  <Field label="ESI number" value={form.esi_no} onChange={(v) => set("esi_no", v)} testid="ed-esi" />
                  <Field label="TAN" value={form.tan} onChange={(v) => set("tan", v)} testid="ed-tan" />
                </Row3>
                <Row3>
                  <Field label="Passport number" value={form.passport_no} onChange={(v) => set("passport_no", v)} testid="ed-passport" />
                  <Field label="Passport expiry" type="date" value={form.passport_expiry?.slice(0, 10) || ""} onChange={(v) => set("passport_expiry", v)} testid="ed-passport-expiry" />
                  <Field label="Driving licence" value={form.driving_license_no} onChange={(v) => set("driving_license_no", v)} testid="ed-dl" />
                </Row3>
              </Section>
            </TabsContent>

            <TabsContent value="history" className="m-0" data-testid="employee-history-panel">
              <Section icon={Briefcase} title="Previous employment">
                <Row3>
                  <Field label="Last employer" value={form.previous_employer} onChange={(v) => set("previous_employer", v)} testid="ed-prev-employer" />
                  <Field label="Last designation" value={form.previous_designation} onChange={(v) => set("previous_designation", v)} testid="ed-prev-designation" />
                  <Field label="Last CTC" type="number" value={form.previous_ctc} onChange={(v) => set("previous_ctc", v)} testid="ed-prev-ctc" placeholder="800000" />
                </Row3>
                <Row3>
                  <Field label="Notice period served (days)" type="number" value={form.notice_period_days} onChange={(v) => set("notice_period_days", v)} testid="ed-notice-days" />
                  <Field label="Total experience (years)" type="number" value={form.total_experience_years} onChange={(v) => set("total_experience_years", v)} testid="ed-total-exp" />
                  <Field label="Reference contact" value={form.reference_contact} onChange={(v) => set("reference_contact", v)} testid="ed-reference" placeholder="HR manager, phone/email" />
                </Row3>
                <div>
                  <Label className="block mb-1.5 text-sm font-semibold">Notice-period / resignation reason</Label>
                  <Textarea rows={3} value={form.notice_period_reason || ""} onChange={(e) => set("notice_period_reason", e.target.value)} className="rounded-none" data-testid="ed-notice-reason" placeholder="Reason for leaving last role" />
                </div>
              </Section>
            </TabsContent>

            <TabsContent value="custom" className="m-0" data-testid="employee-custom-panel">
              <Section icon={Sparkles} title="Custom fields">
                <p className="text-xs text-foreground/60">
                  Free-form key/value pairs specific to your workspace. One entry per line — format is <code className="text-[11px] bg-muted px-1">key: value</code>.
                </p>
                <Textarea
                  rows={10}
                  value={form._custom_fields_text || ""}
                  onChange={(e) => set("_custom_fields_text", e.target.value)}
                  className="rounded-none font-mono text-xs"
                  data-testid="ed-custom-fields"
                  placeholder={"T-shirt size: L\nOffice: Bangalore\nInsurance policy: XYZ-1234"}
                />
              </Section>
            </TabsContent>

            <TabsContent value="changes" className="m-0" data-testid="employee-changes-panel">
              <EmployeeChangeLog eid={eid} />
            </TabsContent>

            <TabsContent value="leave" className="m-0">
              <Section icon={Calendar} title="Leave balance (days)">
                <Row3>
                  <Field label="Sick leave" type="number" value={form.leave_balance_sick} onChange={(v) => set("leave_balance_sick", v)} testid="ed-leave-sick" />
                  <Field label="Casual leave" type="number" value={form.leave_balance_casual} onChange={(v) => set("leave_balance_casual", v)} testid="ed-leave-casual" />
                  <Field label="Earned / privilege leave" type="number" value={form.leave_balance_earned} onChange={(v) => set("leave_balance_earned", v)} testid="ed-leave-earned" />
                </Row3>
                <p className="text-xs text-foreground/60">Leave balances are stored against the employee record. Time-off requests &amp; approvals will plug into these counters when the Leave module ships.</p>
              </Section>
              {/* R153 · Feb 2026 — Comp-Off ledger with earned/consumed/expiring
                  timeline. Only HR admins (or the employee themselves) see it;
                  the endpoint gates access server-side. */}
              <div className="border border-border bg-background p-4 mt-3">
                <CompOffLedger
                  wsId={activeWorkspaceId}
                  userId={emp.user_id || emp.id}
                  canManage={!!(user?.is_super_admin || user?.workspace_role === "admin" || user?.workspace_role === "owner")}
                />
              </div>
            </TabsContent>

            <TabsContent value="payroll" className="m-0">
              <Section icon={Wallet} title="Compensation">
                <Row3>
                  <Field label="Salary amount" type="number" value={form.salary_amount} onChange={(v) => set("salary_amount", v)} testid="ed-salary" placeholder="50000" />
                  <SelectField label="Currency" value={form.salary_currency} onChange={(v) => set("salary_currency", v)} options={CURRENCIES.map((c) => ({ v: c, label: c }))} testid="ed-currency" />
                  <SelectField label="Pay cycle" value={form.pay_cycle} onChange={(v) => set("pay_cycle", v)} options={PAY_CYCLES.map((c) => ({ v: c, label: c }))} testid="ed-pay-cycle" />
                </Row3>
                <Row3>
                  <Field label="Bank name" value={form.bank_name} onChange={(v) => set("bank_name", v)} testid="ed-bank" />
                  <Field label="PAN / Tax ID" value={form.pan} onChange={(v) => set("pan", v)} testid="ed-pan" />
                  <Field label="PF / SS no." value={form.pf_no} onChange={(v) => set("pf_no", v)} testid="ed-pf" />
                </Row3>
                <Row3>
                  <Field label="Account holder name" value={form.bank_account_holder} onChange={(v) => set("bank_account_holder", v)} testid="ed-bank-holder" />
                  <Field label="Account number" value={form.bank_account_number} onChange={(v) => set("bank_account_number", v)} testid="ed-bank-account" />
                  <Field label="IFSC / SWIFT" value={form.bank_ifsc} onChange={(v) => set("bank_ifsc", v)} testid="ed-bank-ifsc" />
                </Row3>
              </Section>
            </TabsContent>
          </Tabs>
        </main>
      </div>
      {linkUserOpen && (
        <CreateLinkUserDialog
          wsId={activeWorkspaceId}
          defaultEmail={form.work_email || form.personal_email || ""}
          defaultName={form.full_name}
          roles={roles}
          onClose={() => setLinkUserOpen(false)}
          onCreated={(newUserId, newMember) => {
            set("user_id", newUserId);
            setMembers((prev) => [...prev, newMember]);
            setLinkUserOpen(false);
          }}
        />
      )}
      {propOpen && propCtx && (
        <EmployeeDesignationPropagationDialog
          open={propOpen}
          onClose={async () => { setPropOpen(false); setPropCtx(null); await load(); }}
          workspaceId={activeWorkspaceId}
          userId={propCtx.userId}
          employeeName={form.full_name}
          oldDesignation={propCtx.oldDesignation}
          newDesignation={propCtx.newDesignation}
          onApplied={async () => { setPropOpen(false); setPropCtx(null); await load(); }}
        />
      )}
    </>
  );
}

function buildForm(emp) {
  const cf = emp.custom_fields || {};
  const cfText = Object.keys(cf).length
    ? Object.entries(cf).map(([k, v]) => `${k}: ${v}`).join("\n")
    : "";
  return {
    // Identity
    emp_id: emp.emp_id || "",
    full_name: emp.full_name || "",
    designation: emp.designation || "",
    designation_id: emp.designation_id || "",
    department: emp.department || "",
    reporting_manager_id: emp.reporting_manager_id || "",
    // Oct 2026 · Linked shift (HR → Leave & Attendance → Shifts).
    shift_id: emp.shift_id || "",
    joining_date: emp.joining_date || "",
    employment_type: emp.employment_type || "",
    status: emp.status || "active",
    work_email: emp.work_email || "",
    phone: emp.phone || "",
    address: emp.address || "",
    user_id: emp.user_id || "",
    timezone: emp.timezone || "",
    // Personal
    date_of_birth: emp.date_of_birth || "",
    gender: emp.gender || "",
    marital_status: emp.marital_status || "",
    nationality: emp.nationality || "",
    blood_group: emp.blood_group || "",
    national_id: emp.national_id || "",
    wedding_anniversary: emp.wedding_anniversary || "",
    celebrations_opt_out: !!emp.celebrations_opt_out,
    // Addresses with geo
    current_address: emp.current_address || "",
    permanent_address: emp.permanent_address || "",
    current_lat: emp.current_lat ?? "",
    current_lng: emp.current_lng ?? "",
    permanent_lat: emp.permanent_lat ?? "",
    permanent_lng: emp.permanent_lng ?? "",
    // Emergency
    emergency_contact_name: emp.emergency_contact_name || "",
    emergency_contact_relation: emp.emergency_contact_relation || "",
    emergency_contact_phone: emp.emergency_contact_phone || "",
    emergency_contact_email: emp.emergency_contact_email || "",
    // Lifecycle
    probation_end_date: emp.probation_end_date || "",
    confirmation_date: emp.confirmation_date || "",
    exit_date: emp.exit_date || "",
    exit_reason: emp.exit_reason || "",
    on_notice_until: emp.on_notice_until || "",
    // Leave balances
    leave_balance_sick: emp.leave_balance_sick ?? "",
    leave_balance_casual: emp.leave_balance_casual ?? "",
    leave_balance_earned: emp.leave_balance_earned ?? "",
    // Payroll
    salary_amount: emp.salary_amount ?? "",
    salary_currency: emp.salary_currency || "",
    pay_cycle: emp.pay_cycle || "",
    bank_name: emp.bank_name || "",
    bank_account_holder: emp.bank_account_holder || "",
    bank_account_number: emp.bank_account_number || "",
    bank_ifsc: emp.bank_ifsc || "",
    pan: emp.pan || "",
    pf_no: emp.pf_no || "",
    // R148g · Family & dependents
    father_name: emp.father_name || "",
    mother_name: emp.mother_name || "",
    spouse_name: emp.spouse_name || "",
    spouse_dob: emp.spouse_dob || "",
    dependent_count: emp.dependent_count ?? "",
    dependent_details: emp.dependent_details || "",
    // R148g · Statutory IDs
    uan: emp.uan || "",
    esi_no: emp.esi_no || "",
    tan: emp.tan || "",
    passport_no: emp.passport_no || "",
    passport_expiry: emp.passport_expiry || "",
    driving_license_no: emp.driving_license_no || "",
    // R148g · Previous employment
    previous_employer: emp.previous_employer || "",
    previous_designation: emp.previous_designation || "",
    previous_ctc: emp.previous_ctc ?? "",
    notice_period_days: emp.notice_period_days ?? "",
    total_experience_years: emp.total_experience_years ?? "",
    reference_contact: emp.reference_contact || "",
    notice_period_reason: emp.notice_period_reason || "",
    // Custom fields — free-form key:value textarea
    _custom_fields_text: cfText,
  };
}

function Row({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <Icon className="w-4 h-4 mt-0.5 text-foreground/60 shrink-0" />
      <div className="min-w-0">
        <div className="label-eyebrow text-[10px]">{label}</div>
        <div className="text-sm break-words">{value || <span className="text-foreground/40">—</span>}</div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="border border-border p-5 space-y-4 bg-background">
      <div className="label-eyebrow inline-flex items-center gap-2">{Icon && <Icon className="w-3.5 h-3.5" />} {title}</div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
function Row2({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }
function Row3({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">{children}</div>; }
function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-10" data-testid={testid} />
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Select value={value || "_none"} onValueChange={(v) => onChange(v === "_none" ? "" : v)}>
        <SelectTrigger className="h-10" data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="_none">—</SelectItem>
          {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function EmployeePerformance({ userId }) {
  const { activeWorkspaceId: wsId } = useAuth();
  const [doc, setDoc] = useState(null);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/workspaces/${wsId}/members/${userId}/ai-summary`)
    .then((r) => setDoc(r.data))
    .catch((e) => { if (e?.response?.status === 403) setDenied(true); });
  useEffect(() => { load(); }, [userId]); // eslint-disable-line
  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/workspaces/${wsId}/members/${userId}/ai-summary`); setDoc(data); toast.success("AI summary generated"); }
    catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  if (denied) return <div className="border border-border p-6 text-sm text-foreground/60" data-testid="perf-denied">Performance insights are visible to HR and this member's reporting manager only.</div>;
  const c = doc?.content;
  const m = doc?.metrics;
  return (
    <div className="space-y-4" data-testid="employee-performance">
      <div className="flex items-center justify-between">
        <div className="text-sm text-foreground/60">AI performance summary (last 30 days){doc && <span className="ml-2 text-xs font-mono">{doc.created_at?.slice(0, 16).replace("T", " ")} UTC</span>}</div>
        <Button size="sm" variant="outline" className="rounded-none" onClick={generate} disabled={busy} data-testid="perf-generate-btn">
          {busy ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}{busy ? "Generating…" : c ? "Refresh" : "Generate"}
        </Button>
      </div>
      {!c && <p className="text-sm text-foreground/60" data-testid="perf-empty">No AI summary yet — click Generate.</p>}
      {c && (
        <div className="border border-border p-5 space-y-3" data-testid="perf-card">
          <p className="text-sm">{c.summary}</p>
          {[["strengths", "Strengths", "text-emerald-600"], ["risks", "Risks", "text-red-600"], ["talking_points", "1:1 talking points", "text-violet-600"]].map(([k, label, tone]) => (
            (c[k] || []).length > 0 && (
              <div key={k}>
                <p className={`text-[10px] uppercase tracking-wide ${tone}`}>{label}</p>
                <ul className="list-disc pl-5 text-sm mt-1 space-y-0.5">{c[k].map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            )
          ))}
        </div>
      )}
      {m && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="perf-metrics">
          {[["Hours (30d)", `${m.hours}h`], ["Utilization", `${m.utilization_pct}%`], ["Billable", `${m.billable_pct}%`],
            ["Violations", m.violations], ["Compliance", `${m.compliance_rate}%`], ["Points", `${m.points_total}${m.rank ? ` · #${m.rank}` : ""}`],
            ["Open tasks", `${m.open_tasks} (${m.overdue_tasks} overdue)`], ["Next 7d planned", `${m.upcoming_planned_hours}h`]].map(([l, v]) => (
            <div key={l} className="border border-border p-3">
              <p className="text-[10px] uppercase tracking-wide text-foreground/50">{l}</p>
              <p className="font-mono font-bold mt-0.5">{v}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
